import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { api, type State } from '../api'
import { Icon } from '../components/Icon'

/**
 * Linking to a page by its NAME, and to a heading inside it.
 *
 * A documentation link is nearly always internal, and the address it needs —
 * `/guides/install/#requirements` — is a thing a writer had to know by heart
 * and type by hand. Getting one character wrong produces a link that looks
 * right, ships, and sends a reader to a 404 that only the link checker ever
 * notices.
 *
 * So this offers the pages that exist, by title, and then the headings inside
 * whichever one is chosen. The headings are read from the page itself when it
 * is picked rather than held for the whole site: they change with every save,
 * and a list gathered on boot would be a list that is quietly wrong by
 * lunchtime.
 *
 * An address typed by hand still works — external links are a real thing, and
 * a picker that refuses them would be a picker people route around.
 */

/** The published address of a page, the same mapping the site build uses. */
function addressOf(id: string): string {
  return id === 'index' ? '/' : `/${id}/`
}

/** `## Requirements` → `requirements`, the same slug the renderer writes. */
function slugOf(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export interface LinkChoice {
  href: string
  /** What the link should SAY, when the writer had selected nothing. */
  text: string
}

export function LinkPicker({
  state,
  branch,
  initial,
  onChoose,
  onClose,
  /** Rendered inside a floating panel at the caret, for the `[[` trigger. */
  at,
}: {
  state: State
  branch: string | null
  initial?: string
  onChoose: (choice: LinkChoice) => void
  onClose: () => void
  at?: { left: number; top: number }
}) {
  const [query, setQuery] = useState(initial ?? '')
  const [index, setIndex] = useState(0)
  /** The page whose headings are being offered, once one is chosen. */
  const [inside, setInside] = useState<{ id: string; title: string } | null>(null)
  const [headings, setHeadings] = useState<{ text: string; slug: string }[] | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const listId = useId()

  const pages = useMemo(() => {
    const q = query.trim().toLowerCase()
    const rows = state.pages
      .filter((p) => p.path.startsWith('pages/'))
      .map((p) => ({ id: p.id, title: p.title ?? p.id }))
    if (!q) return rows.slice(0, 30)
    return rows
      .filter((p) => p.title.toLowerCase().includes(q) || p.id.toLowerCase().includes(q))
      .slice(0, 30)
  }, [state.pages, query])

  /**
   * An address rather than a name: anything with a scheme or a leading slash
   * is taken at its word, and offered as the first choice so somebody pasting
   * a URL is never made to hunt for a page that does not exist.
   */
  const typedAddress = /^(https?:\/\/|mailto:|\/)/i.test(query.trim()) ? query.trim() : null

  useEffect(() => {
    setIndex(0)
  }, [query, inside])

  /** The headings of the chosen page, read when it is chosen. */
  useEffect(() => {
    if (!inside) {
      setHeadings(null)
      return
    }
    let gone = false
    void api
      .page(`pages/${inside.id}.md`, branch ?? undefined)
      .then((page) => {
        if (gone) return
        const found: { text: string; slug: string }[] = []
        for (const line of page.content.split('\n')) {
          const m = /^(#{2,6})\s+(.+?)\s*$/.exec(line)
          if (m) found.push({ text: m[2]!, slug: slugOf(m[2]!) })
        }
        setHeadings(found)
      })
      .catch(() => setHeadings([]))
    return () => {
      gone = true
    }
  }, [inside, branch])

  const headingRows = useMemo(() => {
    if (!headings) return []
    const q = query.trim().toLowerCase()
    return q ? headings.filter((h) => h.text.toLowerCase().includes(q)) : headings
  }, [headings, query])

  /** Every row currently offered, in the order the arrow keys walk them. */
  const rows: { key: string; label: string; hint?: string; run: () => void }[] = inside
    ? [
        {
          key: 'whole',
          label: `The whole page: ${inside.title}`,
          hint: addressOf(inside.id),
          run: () => onChoose({ href: addressOf(inside.id), text: inside.title }),
        },
        ...headingRows.map((h) => ({
          key: `h:${h.slug}`,
          label: h.text,
          hint: `${addressOf(inside.id)}#${h.slug}`,
          run: () => onChoose({ href: `${addressOf(inside.id)}#${h.slug}`, text: h.text }),
        })),
      ]
    : [
        ...(typedAddress
          ? [
              {
                key: 'typed',
                label: `Link to ${typedAddress}`,
                hint: 'the address as typed',
                run: () => onChoose({ href: typedAddress, text: typedAddress }),
              },
            ]
          : []),
        ...pages.map((p) => ({
          key: `p:${p.id}`,
          label: p.title,
          hint: addressOf(p.id),
          /*
           * And the words that found the page are cleared on the way in.
           * They were typed to find "Install it", and leaving them behind
           * filtered the headings by them — so a page whose headings do not
           * happen to repeat its own title offered none of them.
           */
          run: () => {
            setInside(p)
            setQuery('')
          },
        })),
      ]

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIndex((i) => Math.min(i + 1, Math.max(0, rows.length - 1)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIndex((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      rows[index]?.run()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      // Out of the headings first, then out of the picker: Escape undoes one
      // step at a time, which is what makes it safe to press.
      if (inside) {
        setInside(null)
        setQuery('')
      } else onClose()
    } else if (e.key === 'Backspace' && query === '' && inside) {
      e.preventDefault()
      setInside(null)
    }
  }

  useEffect(() => {
    const first = box.current?.querySelector('input')
    first?.focus()
  }, [])

  return (
    /*
     * A combobox, not a dialog.
     *
     * It looked like one — a panel over the page — but it promises none of
     * what `role="dialog"` promises: nothing behind it is suspended, Escape
     * steps back through the headings rather than closing outright, and the
     * keyboard belongs to the field. A field with a list of matches is
     * exactly the combobox pattern, and saying so is what makes the arrow
     * keys and the active row reach somebody listening rather than looking.
     */
    <div
      className="linkpick"
      ref={box}
      style={at ? { position: 'fixed', left: `${at.left}px`, top: `${at.top}px` } : undefined}
    >
      <div className="linkpick__field">
        <Icon name="link" size={13} />
        {inside && <span className="linkpick__crumb truncate">{inside.title}</span>}
        <input
          className="linkpick__input"
          value={query}
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          {...(rows[index] ? { 'aria-activedescendant': `${listId}-${index}` } : {})}
          placeholder={inside ? 'A heading in this page…' : 'A page, or an address'}
          aria-label={inside ? 'Find a heading' : 'Find a page, or type an address'}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
        />
      </div>
      <div className="linkpick__rows" id={listId} role="listbox" aria-label="Matches">
        {rows.length === 0 ? (
          <p className="nothing linkpick__none">
            {inside
              ? 'No heading in that page matches. The whole page is above.'
              : 'No page matches. Type an address to link somewhere else.'}
          </p>
        ) : (
          rows.map((row, i) => (
            <button
              key={row.key}
              id={`${listId}-${i}`}
              type="button"
              role="option"
              aria-selected={i === index}
              className={`linkpick__row${i === index ? ' is-active' : ''}`}
              onMouseEnter={() => setIndex(i)}
              onClick={row.run}
            >
              <span className="linkpick__label truncate">{row.label}</span>
              {row.hint && <span className="linkpick__hint truncate">{row.hint}</span>}
            </button>
          ))
        )}
      </div>
    </div>
  )
}
